<template>
    <div class="page__content invoices">
        <div class="columns">
            <div class="column is-8">
                <h2 class="page__headline">{{ $t('invoices.headline') }}</h2>
                <p class="page__copy" v-html="$t('invoices.copy')"></p>
            </div>
        </div>

        <div class="columns">
            <div class="column is-12">
                <label for="month" class="filter-label">{{ $t('invoices.filter') }}:</label>
                <span class="select">
                    <select name="month" id="month" v-model="selectedMonth">
                        <option v-for="(value, key) in selectableMonths" :value="key">
                          {{ value }}
                        </option>
                    </select>
                </span>

                <span class="select">
                    <select v-model="selectedYear">
                        <option v-for="(value, key) in selectableYears" :value="key">
                          {{ value }}
                        </option>
                    </select>
                </span>
            </div>
        </div>

        <div class="columns">
            <div class="column is-12">
                <table class="table is-striped is-bordered" v-if="invoices.length" :class="{'is-loading': isLoading}">
                    <thead>
                        <tr>
                            <th>
                                <router-link :to="getSortingLink('created_at')" class="header-content">
                                    <span v-if="ordering !== 'created_at' && ordering !== '-created_at'">
                                        {{ $t('invoices.createdAt') }}
                                        <i aria-hidden="true" class="fa fa-sort"></i>
                                    </span>
                                    <span v-else class="has-icons">
                                        <span>{{ $t('invoices.createdAt') }}</span>
                                        <i aria-hidden="true" class="fa" :class="'fa-sort-' + (ordering === 'created_at' ? 'desc': 'asc')"></i>
                                    </span>
                                </router-link>
                            </th>
                            <th>
                                <router-link :to="getSortingLink('sent_to_campground_at')" class="header-content">
                                    <span v-if="ordering !== 'sent_to_campground_at' && ordering !== '-sent_to_campground_at'">
                                        {{ $t('invoices.sentOutAt') }}
                                        <i aria-hidden="true" class="fa fa-sort"></i>
                                    </span>
                                    <span v-else class="has-icons">
                                        <span>{{ $t('invoices.sentOutAt') }}</span>
                                        <i aria-hidden="true" class="fa" :class="'fa-sort-' + (ordering === 'sent_to_campground_at' ? 'desc': 'asc')"></i>
                                    </span>
                                </router-link>
                            </th>
                            <th>
                                <router-link :to="getSortingLink('invoice_number')" class="header-content">
                                    <span v-if="ordering !== 'invoice_number' && ordering !== '-invoice_number'">
                                        {{ $t('invoices.invoiceNumber') }}
                                        <i aria-hidden="true" class="fa fa-sort"></i>
                                    </span>
                                    <span v-else class="has-icons">
                                        <span>{{ $t('invoices.invoiceNumber') }}</span>
                                        <i aria-hidden="true" class="fa" :class="'fa-sort-' + (ordering === 'invoice_number' ? 'desc': 'asc')"></i>
                                    </span>
                                </router-link>
                            </th>
                            <th>
                                <router-link :to="getSortingLink('payout__amount')" class="header-content">
                                    <span v-if="ordering !== 'payout__amount' && ordering !== '-payout__amount'">
                                        {{ $t('invoices.amount') }}
                                        <i aria-hidden="true" class="fa fa-sort"></i>
                                    </span>
                                    <span v-else class="has-icons">
                                        <span>{{ $t('invoices.amount') }}</span>
                                        <i aria-hidden="true" class="fa" :class="'fa-sort-' + (ordering === 'payout__amount' ? 'desc': 'asc')"></i>
                                    </span>
                                </router-link>
                            </th>
                            <th>
                                {{ $t('invoices.download') }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="invoice of invoices" :key="invoice.invoice_number">
                            <td>{{ invoice.created_at | localizedDate('L, LTS') }}</td>
                            <td>{{ invoice.sent_to_campground_at | localizedDate('L, LTS') }}</td>
                            <td>{{ invoice.invoice_number }}</td>
                            <td>{{ invoice.amount.value | currency(invoice.amount.currency, lang) }}</td>
                            <td>
                                <a :href="invoice.pdf_url" target="_blank" class="pdf-link">
                                    <span class="icon">
                                        <i aria-hidden="true" class="fa fa-download"></i>
                                    </span>
                                </a>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <p v-else v-html="$t('invoices.empty')"></p>
            </div>
        </div>

        <div class="columns">
            <div class="column">
                <pagination :loading="isLoading" :params="this.$route.query" :removeFromQuery="['campground']" :currentPage="Number(this.page)" :limit="limit" :numPages="numPages" />
            </div>
        </div>
    </div>
</template>

<script>
import { cloneDeep } from 'lodash';
import {Api} from '../../../api';
import {AppModel} from '../../../models';
import filters from '../../../filters';
import {Pagination} from '../../components';

export default {
    name: 'payments',
    filters: filters,
    components: {
        Pagination,
    },
    data() {
        return {
            invoices: [],
            numPages: 1,
            limit: 10,
            isLoading: true,
            minDate: {
                year: 2018,
                month: 1,
            },
            maxDate: {
                year: new Date().getFullYear(),
                month: 12,
            },
            selectedMonth: '0',
            selectedYear: '0',
            selectableMonths: {},
            selectableYears: {},
        };
    },
    beforeRouteEnter(to, from, next) {
        Api.bootstrap().then(function(response) {
            return Promise.all([response, Api.invoices.fetch(to.query)]);
        }).then(function(response) {
            next((vm) => {
                vm.onInvoicesFetched(response[1].body);
            });
        });
    },
    created() {
        if(this.$route.query.month) {
            this.selectedMonth = this.$route.query.month;
        }
        if(this.$route.query.year) {
            this.selectedYear = this.$route.query.year;
        }

        this.selectableMonths = {
            '0': this.$t('invoices.month'),
        };
        const names = this.$t('generic.calendar.monthnames.long');
        for(let i = 1; i <= 12; i++) {
            this.selectableMonths[i.toString()] = names[i - 1];
        }
        this.calculateSelectableYears();
    },
    computed: {
        lang() {
            return AppModel.lang;
        },
        page() {
            return Number(this.$route.query.page) || 1;
        },
        ordering() {
            return this.$route.query.ordering || 'created_at';
        },
    },
    watch: {
        $route(newValue, oldValue) {
            if(newValue.query !== oldValue.query) {
                this.fetchInvoices();
            }
        },
        selectedMonth() {
            this.onFilterChanged();
        },
        selectedYear() {
            this.onFilterChanged();
        },
    },
    methods: {
        fetchInvoices() {
            this.isLoading = true;
            Api.invoices.fetch(this.$route.query).then((data) => {
                this.onInvoicesFetched(data.body);
            });
        },
        onInvoicesFetched(data) {
            this.invoices = data.results;
            let mind = data.min_date.split('-');
            let maxd = data.max_date.split('-');
            this.minDate = {
                year: Number(mind[0]),
                month: Number(mind[1]),
            };
            this.maxDate = {
                year: Number(maxd[0]),
                month: Number(maxd[1]),
            };
            this.calculateSelectableYears();
            this.numPages = Math.ceil(data.count / this.limit);
            this.isLoading = false;
        },
        onFilterChanged() {
            let query = cloneDeep(this.$route.query);
            query.year = this.selectedYear;
            query.month = this.selectedMonth;
            if(query.year === '0') {
                delete query.year;
            }
            if(query.month === '0') {
                delete query.month;
            }
            delete query.campground;
            this.$router.push({name: this.$route.name, params: this.$route.params, query: query});
        },
        getSortingLink(field) {
            const antiField = `-${field}`;
            let newOrdering;
            if(field === this.ordering) {
                newOrdering = antiField;
            } else {
                newOrdering = field;
            }
            let query = Object.assign({}, this.$route.query, {ordering: newOrdering});
            let params = this.$route.params;
            if(!params.lang) {
                params.lang = this.lang;
            }
            return {
                name: this.$route.name,
                params: params,
                query: query,
            };
        },
        calculateSelectableYears() {
            this.selectableYears = {
                '0': this.$t('invoices.year'),
            };
            for(let y = this.minDate.year; y <= this.maxDate.year; y++) {
                this.selectableYears[y.toString()] = y;
            }
        },
    },
};
</script>
<style lang="scss">
    @import 'invoices.scss';
</style>



// WEBPACK FOOTER //
// src/views/pages/invoices/invoices.vue